import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Inject,
  LOCALE_ID,
  OnDestroy,
  OnInit,
  QueryList,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import { Router, ActivatedRoute, Data, NavigationEnd } from '@angular/router';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MatLegacyTable as MatTable } from '@angular/material/legacy-table';
import { MatLegacyTabGroup as MatTabGroup } from '@angular/material/legacy-tabs';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { MatDrawer } from '@angular/material/sidenav';
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';

import { OnDestroyMixin, untilComponentDestroyed } from '@w11k/ngx-componentdestroyed';
import { Observable, filter, finalize, map, noop, of, switchMap, take, tap, zip } from 'rxjs';

import { AccountService } from 'app/core';
import { FieldDef, User } from 'app/core/models';
import { LayoutService } from 'app/layout/layout.service';
import { ProjectTemplate } from 'app/modules/projects/projects.types';
import { Column, ColumnConfig } from 'app/shared/modules/legacy-tasks/list/list.types';
import { ProjectsService } from 'app/modules/projects/projects.service';
import { LegacyTasksTabService } from '@shared/modules/legacy-tasks/services/tasks.service';
import { defaultTaskTableFields } from 'app/shared/modules/legacy-tasks/list/list.constants';
import { SelectableFieldDef } from 'app/shared/components/column-selector/column-selector.types';
import {
  Task,
  TaskList,
  TaskListTemplate,
  TaskStatus,
  TaskType,
  WorkflowState,
} from 'app/shared/modules/legacy-tasks/tasks.types';
import { SettingsTaskTemplatesCreateTemplateDialog } from './create-task-template-dialog/create-task-list-template-dialog.component';
import { ConfirmService } from 'app/shared/components/widgets/dialog/confirm/confirm.service';
import { ofActionCompleted, Select, Store, Actions } from '@ngxs/store';
import { ObjectsManagerState } from '../../objects-manager/state/state';
import { CustomObject } from 'app/shared/modules/custom-objects/custom-objects.types';
import { ObjectsManagerActions } from '../../objects-manager/state/actions';
import { AccountState } from 'app/state/account/state';
import { FeatureFlag } from 'app/core/enums';
import { TaskActions } from '@tasks/state/actions';
import { TaskExportUtilsService } from '@tasks/services/task-export-utils.service';
import { ConfirmationDialogService } from '@shared/components/widgets/dialog/confirmation/confirmation-dialog.service';

@Component({
  selector: 'task-list-templates-details',
  templateUrl: './details.component.html',
  styleUrls: ['./details.component.scss', './../../../../shared/modules/legacy-tasks/list/table/table.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TaskListTemplatesDetailsComponent extends OnDestroyMixin implements OnInit, OnDestroy {
  public taskListTemplate: TaskListTemplate;
  public taskList: TaskList;
  public projectTemplates: Array<ProjectTemplate<any>>;
  public taskListTemplateForm: UntypedFormGroup;
  public resolversData: Data;
  public taskFieldDefs: Array<FieldDef> = [];
  public selectedFields: SelectableFieldDef[] = [];
  public selectedOrder: Array<string> = [];
  public tasks: Array<Task>;
  private selectedTask: Task;
  public workflowStates: Array<TaskStatus>;
  public columns: Array<Column> = [];
  private customObjectLabelMap: Map<string, string> = new Map<string, string>();
  public isTaskListLoading: boolean;
  public taskCustomFieldsEnabled = false;
  public drawerOpened = false;
  private columnsConfig: ColumnConfig = {
    prefix: ['drag_control', 'expand_control', 'status_control'],
    dynamic: [],
    suffix: ['details_control'],
  };
  activeTab = 'info';

  users: User[];

  @Select(ObjectsManagerState.get) customObjects$: Observable<CustomObject[]>;

  @ViewChild('taskTemplateTabGroup') private tabGroup: MatTabGroup;
  @ViewChildren('childTable') matTables: QueryList<MatTable<any>>;
  @ViewChild('matDrawer', { static: true }) matDrawer: MatDrawer;

  get dynamicColumns(): Array<Column> {
    return this.columnsConfig.dynamic;
  }

  get columnsHeaders(): Array<string> {
    return this.columns.map((column) => (typeof column === 'string' ? column : column.name));
  }

  get selectedTaskListTemplate(): TaskListTemplate {
    return this.activatedRoute.snapshot.data.taskListTemplate;
  }

  constructor(
    @Inject(LOCALE_ID) public locale: string,
    private readonly router: Router,
    private readonly snackBar: MatSnackBar,
    private readonly matDialog: MatDialog,
    private readonly cdRef: ChangeDetectorRef,
    private readonly activatedRoute: ActivatedRoute,
    private readonly formBuilder: UntypedFormBuilder,
    private readonly accountService: AccountService,
    private readonly projectsService: ProjectsService,
    private readonly tasksTabService: LegacyTasksTabService,
    private readonly layoutService: LayoutService,
    private readonly confirmService: ConfirmService,
    private readonly confirmDialogService: ConfirmationDialogService,
    private readonly taskExportUtilsService: TaskExportUtilsService,
    private readonly store: Store,
    private readonly actions: Actions,
  ) {
    super();
  }

  private isTaskListUrlPattern(url: string): boolean {
    const regex = /\/settings\/task-lists\/\d+\/\d+\/\d+$/;
    return regex.test(url);
  }

  tabChange(event: any): void {
    if (this.activeTab === event) {
      return;
    }

    this.activeTab = event;
    if (this.activeTab === 'configuration') {
      this.router.navigate(['newtasks', 'TaskListTemplate', this.taskListTemplate.id], {
        relativeTo: this.activatedRoute,
      });
    } else {
      this.router.navigate(['./'], {
        relativeTo: this.activatedRoute.parent,
      });
    }
  }

  ngOnInit(): void {
    this.buildForm();

    this.taskCustomFieldsEnabled = this.store
      .selectSnapshot(AccountState.getAccount)
      ?.features.includes(FeatureFlag.TASKS_CUSTOM_FIELDS);

    this.customObjects$
      .pipe(
        filter((response) => !!response.length),
        take(1),
      )
      .subscribe((customObjects) => {
        customObjects.forEach((customObject) => {
          this.customObjectLabelMap.set(customObject.name, customObject.label);
        });
      });

    this.store.dispatch(new ObjectsManagerActions.Get());
    this.getDataFromResolvers();
    this.setupTaskListData();
    this.fetchProjectTemplates();
    this.setupTaskListTemplate();
    this.getTaskStatuses();
    this.layoutService.emitCommon('openDrawer', true, 'subject');

    // MJR: This is a hack to force the task details to close properly
    this.router.events.pipe(untilComponentDestroyed(this)).subscribe((event) => {
      if (event instanceof NavigationEnd && !this.isTaskListUrlPattern(event.url)) {
        this.drawerOpened = false;
        this.selectedTask = null;
        this.layoutService.emitCommon('openDrawer', false, 'subject');
        this.cdRef.detectChanges();
      }
    });

    this.actions.pipe(ofActionCompleted(TaskActions.UpdateTask), untilComponentDestroyed(this)).subscribe(() => {
      this.setupTaskListData(false);
    });

    this.layoutService
      .getCommon('openDrawer')
      .pipe(
        untilComponentDestroyed(this),
        filter((x) => !!x),
      )
      .subscribe(() => this.matDrawer.open());
  }

  ngOnDestroy(): void {
    this.layoutService
      .getCommon('openDrawer')
      .pipe(
        untilComponentDestroyed(this),
        filter((x) => !!x),
      )
      .subscribe(() => this.matDrawer.close());

    super.ngOnDestroy();
  }

  private getDataFromResolvers(): void {
    this.activatedRoute.data.pipe(untilComponentDestroyed(this)).subscribe((data: Data) => {
      this.resolversData = data;
      this.taskListTemplate = data.taskListTemplate;
      this.taskListTemplateForm.patchValue(this.taskListTemplate);
      if (this.activatedRoute?.firstChild?.firstChild?.snapshot?.params) {
        const { taskRelatedTo, taskRelatedToId } = this.activatedRoute?.firstChild?.firstChild.snapshot.params;
        if (taskRelatedTo && taskRelatedToId) {
          this.activeTab = 'configuration';
        }
      }
      this.cdRef.detectChanges();
    });

    this.accountService.users$.pipe(untilComponentDestroyed(this)).subscribe((users: User[]) => {
      this.users = users;
    });
  }

  getRelatedToTypeLabel(relatedToType: string): string {
    return relatedToType.startsWith('co_') ? this.customObjectLabelMap.get(relatedToType) : relatedToType;
  }

  public buildForm(): void {
    this.taskListTemplateForm = this.formBuilder.group({
      id: [''],
      name: ['', [Validators.required]],
      internal_value: [''],
      associated_project_template_ids: [''],
    });
  }

  private setupTaskListData(showLoading = true): void {
    this.isTaskListLoading = showLoading;
    this.tasksTabService
      .getTaskListById(this.selectedTaskListTemplate.task_list_id.toString(), this.selectedTaskListTemplate.id)
      .pipe(
        untilComponentDestroyed(this),
        switchMap(() => {
          return this.tasksTabService
            .getTasks(this.selectedTaskListTemplate.task_list_id, this.selectedTaskListTemplate.id, {}, '')
            .pipe(
              untilComponentDestroyed(this),
              tap((tasks) => {
                this.tasks = this.tasksTabService.orderNestedTasks(tasks);
                this.handleTableRenderForSelectedTask();
              }),
            );
        }),
        finalize(() => {
          this.isTaskListLoading = false;
          this.cdRef.detectChanges();
        }),
      )
      .subscribe();

    this.tasksTabService.taskList$.pipe(untilComponentDestroyed(this)).subscribe((taskList: TaskList) => {
      this.taskList = taskList;
      this.setupTaskListTemplate();
      this.cdRef.markForCheck();
    });

    this.tasksTabService.task$.pipe(untilComponentDestroyed(this)).subscribe((task: Task) => {
      if (task) {
        this.selectedTask = task;
        const parentId = this.selectedTask.parent_id;
        const parent = this.tasks?.find(({ id }) => id === parentId);
        let index = -1;
        if ((index = parent?.children?.findIndex(({ id }) => id === -1)) > -1) {
          parent.children[index].id = this.selectedTask.id;
          this.renderChildTableRows(parentId);
        }
      }
      this.cdRef.detectChanges();
    });
  }

  private fetchProjectTemplates(): void {
    this.projectsService
      .getProjectTemplates()
      .pipe(
        untilComponentDestroyed(this),
        map((projectTemplates: ProjectTemplate<any>[]) => projectTemplates.filter((template) => !template.hidden)),
      )
      .subscribe((projectTemplates: ProjectTemplate<any>[]) => {
        if (projectTemplates) {
          this.projectTemplates = projectTemplates;
        }
        this.cdRef.markForCheck();
      });
  }

  private setupTaskListTemplate(): void {
    if (this.taskListTemplate?.definition?.fields?.length) {
      const [firstItem] = this.taskListTemplate.definition?.fields;

      const fields =
        typeof firstItem === 'string'
          ? this.taskListTemplate.definition?.fields
          : this.taskListTemplate.definition?.fields.map((field: any) => field.name);

      this.refreshSelectedFields(fields.map((field: string) => this.lookupField(field)) as SelectableFieldDef[]);
    } else {
      this.refreshSelectedFields(defaultTaskTableFields as SelectableFieldDef[]);
    }
  }

  private getTaskStatuses(): void {
    this.tasksTabService
      .getTaskStatuses()
      .pipe(untilComponentDestroyed(this))
      .subscribe((taskStatuses) => {
        this.workflowStates = taskStatuses;
      });
  }

  public saveGeneralInfo(): void {
    const template = this.taskListTemplateForm.getRawValue();
    this.tasksTabService
      .updateTaskListTemplate(template)
      .pipe(untilComponentDestroyed(this))
      .subscribe({
        next: (res) => {
          this.taskListTemplate = res;
          this.snackBar.open('Succesfully saved template.', null, {
            duration: 3000,
          });
        },
        error: () => {
          this.snackBar.open('Unable to save your changes at this time. Try again later.', null, {
            duration: 3000,
            panelClass: 'error-snackbar',
          });
        },
        complete: () => this.cdRef.detectChanges(),
      });
  }

  public deleteTaskTemplate(): void {
    this.confirmService
      .openConfirmationModal()
      .pipe(filter((result: boolean) => result))
      .subscribe(() => {
        this.tasksTabService
          .deleteTaskListTemplate(this.taskListTemplate.id)
          .pipe(untilComponentDestroyed(this))
          .subscribe(() => this.router.navigate(['/settings/task-lists']));
      });
  }

  public downloadTasks(format: 'csv' | 'pdf' = 'csv'): void {
    const selectedFields = this.selectedFields.map(({ name }) => name) || [];

    this.tasksTabService
      .getTasks(this.taskList?.id, this.taskListTemplate.id, {}, format, true, selectedFields.join(','))
      .pipe(untilComponentDestroyed(this))
      .subscribe((data: any) => this.taskExportUtilsService.handleTaskExport(data, format, this.taskList?.name));
  }

  public isGeneralInfoTabActive(): boolean {
    return this.activeTab === 'info';
  }

  private lookupField(name: string): FieldDef | undefined {
    return this.resolversData.taskFieldDefs.find((fieldDef: FieldDef) => fieldDef.name === name);
  }

  public onSaveColumns(selectedFieldDefs: SelectableFieldDef[]): void {
    selectedFieldDefs = selectedFieldDefs.length ? selectedFieldDefs : (defaultTaskTableFields as SelectableFieldDef[]);

    this.selectedOrder = selectedFieldDefs.map(({ name }) => name) || [];

    this.tasksTabService
      .updateTaskListTemplate({
        ...this.taskListTemplate,
        definition: {
          ...this.taskListTemplate.definition,
          fields: selectedFieldDefs.map(({ name }) => name),
        },
      })
      .pipe(untilComponentDestroyed(this))
      .subscribe((taskListTemplate: TaskListTemplate) => (this.taskListTemplate = taskListTemplate));

    this.refreshSelectedFields(selectedFieldDefs);
  }

  private refreshSelectedFields(selectedFieldDefs: SelectableFieldDef[]): void {
    this.selectedFields = selectedFieldDefs;
    this.columnsConfig.dynamic = this.selectedFields;
    this.columns = [...this.columnsConfig.prefix, ...this.columnsConfig.dynamic, ...this.columnsConfig.suffix];

    this.taskFieldDefs = [
      ...this.resolversData.taskFieldDefs.map((taskFieldDef: FieldDef) => {
        const selected = !!this.selectedFields.find(
          (selectedField: SelectableFieldDef) => selectedField.name === taskFieldDef.name,
        );

        return {
          ...taskFieldDef,
          selected,
        };
      }),
    ];

    this.selectedOrder = this.selectedFields.map(({ name }) => name) || [];
  }

  public updateTask(task: Task): void {
    this.tasksTabService
      .updateTask(this.taskList.id, this.selectedTaskListTemplate.id, task, false, false)
      .pipe(
        untilComponentDestroyed(this),
        switchMap((task: Task) => {
          return zip(of(task), this.tasksTabService.tasks$);
        }),
        switchMap(([updatedTask, tasks]) => {
          const flattenedTasks = this.tasksTabService.flattenTasks(tasks);

          if (updatedTask.task_type === 'task' && updatedTask.parent_id) {
            const notInProgressStates: WorkflowState[] = ['n_a', 'not_started'];
            const { id: taskId, parent_id: parentId, task_list_id: taskListId } = updatedTask;
            const children = flattenedTasks.filter(({ id, parent_id }) => parent_id === parentId && id !== taskId);
            children.push(updatedTask);

            let parentWorkflowState: WorkflowState;

            if (children.every(({ workflow_state }) => workflow_state === 'n_a')) {
              parentWorkflowState = 'n_a';
            } else if (children.every(({ workflow_state }) => workflow_state === 'complete')) {
              parentWorkflowState = 'complete';
            } else if (children.some(({ workflow_state }) => !notInProgressStates.includes(workflow_state))) {
              parentWorkflowState = 'in_progress';
            } else {
              parentWorkflowState = 'not_started';
            }
            const parentTask = flattenedTasks.find(({ id }) => id === parentId);
            parentTask.workflow_state = parentWorkflowState;

            if (parentTask.task_type !== 'section') {
              return this.tasksTabService.updateTask(
                taskListId,
                this.selectedTaskListTemplate.id,
                parentTask,
                false,
                true,
              );
            }
          }
          return this.tasksTabService.getTaskListById(this.taskList.id.toString(), this.selectedTaskListTemplate.id);
        }),
        switchMap(() => this.tasksTabService.getTasks(this.taskList.id, this.selectedTaskListTemplate.id)),
        finalize(() => this.cdRef.detectChanges()),
      )
      .subscribe();
  }

  public navigateToTask(row: Task): void {
    this.layoutService.emitCommon('openDrawer', true, 'subject');
    this.drawerOpened = true;
    const isTaskSelected = this.selectedTask?.id && this.matDrawer?.opened;
    let route = this.activatedRoute;
    while (route.firstChild) {
      route = route.firstChild;
    }

    if (isTaskSelected) {
      let url: any = this.router.url.split('/');
      url.pop();
      url.pop();
      url = url.join('/');
      this.router.navigateByUrl(url + '/' + row.task_list_id + '/' + row.id);
    } else {
      this.router.navigate(['./', row.task_list_id, row.id], { relativeTo: route });
    }
  }

  public createTask(model: { taskType: TaskType; parentId?: number; rank?: number }): void {
    if (!model.rank) {
      const { parentId, taskType } = model;

      let ranks = this.tasks.map((task) => task.rank);
      let rank = ranks?.length ? Math.max(...ranks) + 1 : 0;

      let childTasks = this.tasks.filter(
        (task) => (task.task_type === 'task' || task.task_type === 'key_date') && task.parent_id == parentId,
      );

      const isTaskSelected = this.selectedTask?.id && this.matDrawer?.opened;
      let index = 0;
      if (parentId) {
        let parentIndex = this.tasks.findIndex((i) => i.id == parentId);

        if (childTasks?.length) {
          index = parentIndex + childTasks.length + 1;
        } else {
          index = this.tasks.findIndex((i) => i.id == parentId) + 1;
        }
      } else if (isTaskSelected && (taskType == 'task' || taskType == 'key_date')) {
        index = this.tasks.findIndex((i) => i.id == this.selectedTask.id) + 1;
      } else if (taskType == 'section') {
        index = rank;
      }

      let task = {
        task_type: taskType,
        task_list_id: this.taskList.id,
        rank: index,
        parent_id: parentId,
      };

      this.tasksTabService
        .createTask(this.taskList.id, task, index)
        .pipe(untilComponentDestroyed(this))
        .subscribe((newTask: Task) => {
          this.resetTaskOrder(newTask, childTasks);
          let route = this.activatedRoute;
          while (route.firstChild) {
            route = route.firstChild;
          }

          if (isTaskSelected) {
            let url: any = this.router.url.split('/');
            url.pop();
            url = url.join('/');
            this.router.navigateByUrl(url + '/' + newTask.id);
          } else {
            this.router.navigate(['./', newTask.id], { relativeTo: route });
          }
        });
    } else {
      const { parentId, taskType, rank } = model;

      const task = {
        task_type: taskType,
        task_list_id: this.taskList.id,
        rank,
        parent_id: parentId,
      };

      const isTaskSelected = this.selectedTask?.id && this.matDrawer?.opened;

      this.tasksTabService
        .createTask(this.taskList.id, task, null, null, true, false)
        .pipe(untilComponentDestroyed(this))
        .subscribe((newTask: Task) => {
          let route = this.activatedRoute;
          while (route.firstChild) {
            route = route.firstChild;
          }

          if (isTaskSelected) {
            let url: any = this.router.url.split('/');
            url.pop();
            url.pop();
            url = url.join('/');
            this.router.navigateByUrl(url + '/' + newTask.id);
          } else {
            this.router.navigate(['./', newTask.task_list_id, newTask.id], { relativeTo: route });
          }
        });
    }
  }

  resetTaskOrder(task: Task, tasks: Task[]): void {
    let index = 0;
    for (let task of tasks) {
      task.rank = index;
      index += 1;
    }

    this.tasksTabService
      .updateTasksOrders(this.taskList, tasks)
      .pipe(untilComponentDestroyed(this))
      .subscribe(() => {
        if (task) {
          let latest = document.getElementById('task-' + task.id);
          if (latest) latest.scrollIntoView({ behavior: 'smooth', block: 'end', inline: 'nearest' });
        }
      });
  }

  onOrderChanged(changedTasks: Task[]): void {
    this.tasksTabService
      .updateTasksOrders(this.taskList, changedTasks)
      .pipe(untilComponentDestroyed(this))
      .subscribe(noop);
  }

  public createdTaskBasedOnType(model: { taskType: TaskType; parentId: number }): void {
    const { taskType, parentId } = model;
    const placeholderTask: Partial<Task> = {
      id: -1,
      rank: this.getNextRank(parentId),
      task_type: taskType,
      title: '',
      workflow_state: 'not_started',
      children: [],
      custom_field_values: [],
    };
    this.tasks.find(({ id }) => id === parentId)?.children?.push(placeholderTask);
    this.createTask({ ...model, rank: placeholderTask.rank });
  }

  public saveAsTemplate(): void {
    const dialogRef = this.matDialog.open(SettingsTaskTemplatesCreateTemplateDialog, {
      panelClass: 'mailbox-compose-dialog',
      data: {
        taskListId: this.taskList.id,
        createNewTemplate: false,
        saveAsTemplate: true,
      },
    });

    dialogRef
      .afterClosed()
      .pipe(
        untilComponentDestroyed(this),
        finalize(() => this.cdRef.detectChanges()),
      )
      .subscribe((result: { task_list_template: TaskListTemplate }) => {
        if (result?.task_list_template?.id) {
          this.taskList.task_list_template_id = result.task_list_template.id;
          this.taskList.task_list_template_name = result.task_list_template.name;
          this.tasksTabService
            .updateTaskList(this.taskList, this.taskListTemplate.id)
            .pipe(untilComponentDestroyed(this))
            .subscribe();
          this.snackBar.open('Template created.', '', {
            duration: 3000,
          });
        }
      });
  }

  public updateExistingTaskLists(): void {
    this.confirmDialogService
      .openConfirmationDialog({
        renderHtml: true,
        dialogTitle: 'Update Layout on related Task Lists',
        description: `Clicking "yes" will override the columns displayed on the table and custom fields displayed on the tasks.
          New tasks or changes to the values on the existing tasks won't be affected.
          <br /> <br />
          Do you want to proceed?`,
      })
      .pipe(filter((result: boolean) => result))
      .subscribe(() => {
        this.tasksTabService
          .applyTemplateDefinitionToExistingTaskLists(this.taskListTemplate.id)
          .pipe(untilComponentDestroyed(this))
          .subscribe(() => {
            this.snackBar.open('Succesfully updated related task lists.', null, {
              duration: 3000,
            });
          });
      });
  }

  private handleTableRenderForSelectedTask(): void {
    if (this.selectedTask?.parent_id) {
      this.renderChildTableRows(this.selectedTask?.parent_id);
    }
  }

  private renderChildTableRows(parentTaskId: number): void {
    const matTableClass = `child-table-${parentTaskId}`;
    this.matTables
      .find(({ _elementRef: { nativeElement } }: any) => nativeElement.classList.contains(matTableClass))
      ?.renderRows();
  }

  public isSubtask(task: Task): boolean {
    return this.tasksTabService.isSubtask(task, this.tasks);
  }

  public sectionClick(task: Task): void {
    task.expanded = !task.expanded;
    this.tasksTabService
      .updateTask(this.taskList.id, this.selectedTaskListTemplate.id, task, false, false)
      .pipe(untilComponentDestroyed(this))
      .subscribe();
  }

  public whenIsKeyDate(index: number, task: Task): boolean {
    return task.task_type === 'key_date';
  }

  public whenIsTask(index: number, task: Task): boolean {
    return task.task_type === 'task';
  }

  private getNextRank(parentId: number): number {
    const ranks = [0, ...this.tasks.find(({ id }) => id === parentId)?.children?.map(({ rank }) => rank)];
    return Math.max(...ranks) + 1 || 0;
  }

  public onDrawerClosed(): void {
    return this.layoutService.emitCommon('openDrawer', false, 'subject');
  }
}
